import { Injectable, Inject, DOCUMENT } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";

export interface SeoMeta {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: "summary" | "summary_large_image";
  keywords?: string;
  robots?: string;
  jsonLd?: any;
}

const OG_TAGS = ["og:title", "og:description", "og:url", "og:image", "og:type", "og:site_name"];
const TWITTER_TAGS = ["twitter:card", "twitter:title", "twitter:description", "twitter:image"];

@Injectable({ providedIn: "root" })
export class SeoService {
  private _jsonLdEl: HTMLScriptElement | null = null;

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  setPageMeta(seo: SeoMeta): void {
    this.title.setTitle(seo.title);

    if (seo.description) {
      this._upsertName("description", seo.description);
    }

    if (seo.keywords) {
      this._upsertName("keywords", seo.keywords);
    }

    if (seo.robots) {
      this._upsertName("robots", seo.robots);
    }

    this._setCanonical(seo.canonical);

    this._upsertProperty("og:title", seo.title);
    if (seo.description) this._upsertProperty("og:description", seo.description);
    if (seo.canonical) this._upsertProperty("og:url", seo.canonical);
    if (seo.ogImage) this._upsertProperty("og:image", seo.ogImage);
    this._upsertProperty("og:type", seo.ogType || "website");
    this._upsertProperty("og:site_name", "MyFender");

    const twitterCard = seo.twitterCard || (seo.ogImage ? "summary_large_image" : "summary");
    this._upsertName("twitter:card", twitterCard);
    this._upsertName("twitter:title", seo.title);
    if (seo.description) this._upsertName("twitter:description", seo.description);
    if (seo.ogImage) this._upsertName("twitter:image", seo.ogImage);

    this._setJsonLd(seo.jsonLd);
  }

  clearJsonLd(): void {
    if (this._jsonLdEl && this._jsonLdEl.parentNode) {
      this._jsonLdEl.parentNode.removeChild(this._jsonLdEl);
    }
    this._jsonLdEl = null;
  }

  private _upsertName(name: string, content: string) {
    if (this.meta.getTag(`name="${name}"`)) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.addTag({ name, content });
    }
  }

  private _upsertProperty(property: string, content: string) {
    if (this.meta.getTag(`property="${property}"`)) {
      this.meta.updateTag({ property, content }, `property="${property}"`);
    } else {
      this.meta.addTag({ property, content });
    }
  }

  private _setCanonical(href?: string) {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!href) {
      if (link) link.parentNode?.removeChild(link);
      return;
    }
    if (!link) {
      link = this.doc.createElement("link");
      link.setAttribute("rel", "canonical");
      this.doc.head.appendChild(link);
    }
    link.setAttribute("href", href);
  }

  private _setJsonLd(data: any) {
    this.clearJsonLd();
    if (!data) return;
    const script = this.doc.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    this.doc.head.appendChild(script);
    this._jsonLdEl = script;
  }
}
